import { useState, useEffect, useRef, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import axios from "axios";
import { toast } from "sonner";
import { Table, CloudArrowUp, Trash, FileXls, FilePdf, DotsSixVertical, EnvelopeSimple, WhatsappLogo, MicrosoftOutlookLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { useSettings } from "@/contexts/SettingsContext";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, rectSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileUrl = (gridfsId) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${gridfsId}`;

// Tenta partilhar o FICHEIRO real (não o link) através da Web Share API nativa do dispositivo -
// o utilizador escolhe a app (Email, Outlook, WhatsApp...) e o ficheiro vai anexado de verdade.
// Não é possível anexar um ficheiro remoto a um link mailto:/ms-outlook: (limitação da plataforma
// web), por isso quando a partilha nativa não está disponível (ex: browser de computador) o
// ficheiro é descarregado e o utilizador tem de o anexar manualmente.
const shareFileNative = async (url, filename, title) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: title || filename });
      return true;
    }
  } catch (error) {
    if (error?.name === 'AbortError') return true; // utilizador cancelou a folha de partilha
    console.error('Partilha nativa falhou:', error);
  }
  return false;
};

const downloadFileForManualAttach = (url, filename, appLabel) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  toast.info(`Ficheiro descarregado - anexe-o manualmente no ${appLabel}`);
};

const shareByEmail = async (doc) => {
  const url = fileUrl(doc.gridfs_id);
  const shared = await shareFileNative(url, doc.name, `Tabela: ${doc.name}`);
  if (shared) return;
  downloadFileForManualAttach(url, doc.name, 'seu cliente de email');
  window.location.href = `mailto:?subject=${encodeURIComponent(`Tabela: ${doc.name}`)}`;
};

const shareByOutlook = async (doc) => {
  const url = fileUrl(doc.gridfs_id);
  const shared = await shareFileNative(url, doc.name, `Tabela: ${doc.name}`);
  if (shared) return;
  downloadFileForManualAttach(url, doc.name, 'Outlook');
  const subject = encodeURIComponent(`Tabela: ${doc.name}`);
  const appUrl = `ms-outlook://compose?subject=${subject}`;
  const mailtoUrl = `mailto:?subject=${subject}`;
  const timer = setTimeout(() => {
    if (!document.hidden) {
      window.location.href = mailtoUrl;
    }
  }, 1200);
  document.addEventListener('visibilitychange', () => clearTimeout(timer), { once: true });
  window.location.href = appUrl;
};

const shareByWhatsapp = (doc) => {
  const url = fileUrl(doc.gridfs_id);
  const text = encodeURIComponent(`Tabela: ${doc.name}\n${url}`);
  window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
};

function SortableTabelaCard({ doc, isReadOnly, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: doc.id, disabled: isReadOnly });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group border border-stone-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-all duration-200 relative"
      data-testid={`tabela-${doc.id}`}
    >
      {!isReadOnly && (
        <button
          {...attributes}
          {...listeners}
          className="absolute top-1.5 left-1.5 z-10 p-1 bg-white/90 rounded-md text-stone-400 hover:text-stone-600 cursor-grab active:cursor-grabbing touch-none"
          data-testid={`drag-handle-tabela-${doc.id}`}
          title="Arrastar para reordenar"
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
      )}
      <a
        href={fileUrl(doc.gridfs_id)}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-[3/4] bg-stone-50 flex items-center justify-center overflow-hidden"
        data-testid={`btn-open-tabela-${doc.id}`}
      >
        {doc.cover_gridfs_id ? (
          <img
            src={fileUrl(doc.cover_gridfs_id)}
            alt={doc.name}
            className="w-full h-full object-cover"
          />
        ) : doc.file_type === 'excel' ? (
          <FileXls size={56} className="text-green-600" weight="duotone" />
        ) : (
          <FilePdf size={56} className="text-red-500" weight="duotone" />
        )}
      </a>
      <div className="p-2.5">
        <p className="text-xs font-medium text-stone-700 truncate mb-1.5" title={doc.name}>
          {doc.name}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => shareByEmail(doc)}
            className="flex-1 flex items-center justify-center py-1.5 text-sky-500 hover:bg-sky-50 rounded-md transition-colors"
            data-testid={`btn-share-email-tabela-${doc.id}`}
            title="Partilhar por Email"
          >
            <EnvelopeSimple size={15} />
          </button>
          <button
            onClick={() => shareByOutlook(doc)}
            className="flex-1 flex items-center justify-center py-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            data-testid={`btn-share-outlook-tabela-${doc.id}`}
            title="Partilhar por Outlook"
          >
            <MicrosoftOutlookLogo size={15} />
          </button>
          <button
            onClick={() => shareByWhatsapp(doc)}
            className="flex-1 flex items-center justify-center py-1.5 text-green-500 hover:bg-green-50 rounded-md transition-colors"
            data-testid={`btn-share-whatsapp-tabela-${doc.id}`}
            title="Partilhar por WhatsApp"
          >
            <WhatsappLogo size={15} />
          </button>
          {!isReadOnly && (
            <button
              onClick={() => onDelete(doc.id)}
              className="flex-1 flex items-center justify-center py-1.5 text-stone-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
              data-testid={`btn-delete-tabela-${doc.id}`}
              title="Eliminar"
            >
              <Trash size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Tabelas() {
  const { isReadOnly } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tabelas`);
      setItems(res.data);
    } catch (error) {
      toast.error("Erro ao carregar tabelas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const valid = files.filter((f) => ['pdf', 'xlsx', 'xls'].includes(f.name.split('.').pop().toLowerCase()));
    const invalidCount = files.length - valid.length;
    if (invalidCount > 0) {
      toast.error(`${invalidCount} ficheiro(s) ignorado(s) - apenas PDF ou Excel (.xlsx/.xls) são permitidos`);
    }
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < valid.length; i++) {
      setUploadProgress({ current: i + 1, total: valid.length });
      try {
        const formData = new FormData();
        formData.append('file', valid[i]);
        await axios.post(`${API}/tabelas/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        });
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(successCount === 1 ? "Tabela carregada com sucesso" : `${successCount} tabelas carregadas com sucesso`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} ficheiro(s) falharam ao carregar`);
    }

    fetchItems();
    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tabelas/${deleteId}`);
      toast.success("Tabela eliminada");
      fetchItems();
    } catch (error) {
      toast.error("Erro ao eliminar tabela");
    } finally {
      setDeleteId(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    try {
      await axios.put(`${API}/tabelas/reorder`, { ordered_ids: reordered.map((it) => it.id) });
    } catch (error) {
      toast.error("Erro ao guardar a nova ordem");
      fetchItems();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tabelas-page">
      <PageHeader
        icon={Table}
        title="Documentos"
        subtitle="Listas de preços em PDF ou Excel"
        actions={!isReadOnly && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              accept=".pdf,.xlsx,.xls"
              multiple
              className="hidden"
              data-testid="input-upload-tabela"
            />
            <Button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="btn-upload-tabela"
            >
              <CloudArrowUp size={18} />
              {uploading ? `A carregar ${uploadProgress?.current || ''}/${uploadProgress?.total || ''}...` : "Carregar Ficheiros"}
            </Button>
          </>
        )}
      />

      <div className="gresart-card">
        {items.length === 0 ? (
          <div className="empty-state py-16">
            <Table size={64} className="text-stone-300 mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">Sem tabelas</h3>
            <p className="text-stone-500 mb-6 max-w-md">
              Carregue as suas listas de preços em PDF ou Excel para as ter sempre à mão
            </p>
            {!isReadOnly && (
              <Button className="btn-primary" onClick={() => fileInputRef.current?.click()} data-testid="btn-upload-first-tabela">
                <CloudArrowUp size={18} />
                Carregar Ficheiros
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="tabelas-grid">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((it) => it.id)} strategy={rectSortingStrategy}>
                {items.map((doc) => (
                  <SortableTabelaCard key={doc.id} doc={doc} isReadOnly={isReadOnly} onDelete={setDeleteId} />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={!!deleteId}
        onOpenChange={(v) => !v && setDeleteId(null)}
        onConfirm={handleDelete}
        itemType="tabela"
      />
    </div>
  );
}
